import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { ACCOUNT_COLUMNS } from '@/types/filterKeyConst';
import { buildObjQuery } from '@/utils/util';

export const useAccountFilterMap = () => {
  const workspaceFieldOptions = useWorkspaceStore(
    (state) => state.workspaceFieldOptions,
  );

  const fieldOptions: StructuredFieldOptionMap = {
    [ACCOUNT_COLUMNS.NAME_SEARCH]: {
      columnKey: ACCOUNT_COLUMNS.NAME_SEARCH,
      columnName: 'Name',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'pagination',
        filterItemFetcher: async (params) => {
          const cursor = params?.cursor;
          const searchValue = params?.searchValue;
          const queryObj: QueryObj = {
            account_name: searchValue || null,
            cursor: cursor || null,
            direction: 'next',
            sortby: 'name',
            order: 'asc',
          };
          const queryString = buildObjQuery(queryObj);
          const res = await service.account.fetchCompanyList(queryString);
          return res.map((company) => ({
            columnKey: ACCOUNT_COLUMNS.NAME_SEARCH,
            filterKey: company.name,
            filterName: company.name,
            filterOptionNode: company.name,
            cursor: company.paginate_token,
          }));
        },
      },
    },
    [ACCOUNT_COLUMNS.INDUSTRY]: {
      columnKey: ACCOUNT_COLUMNS.INDUSTRY,
      columnName: 'Industry',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return workspaceFieldOptions.industry_values
            .map((value) => ({
              columnKey: ACCOUNT_COLUMNS.INDUSTRY,
              filterKey: value,
              filterName: value,
              filterOptionNode: value,
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [ACCOUNT_COLUMNS.RATING]: {
      columnKey: ACCOUNT_COLUMNS.RATING,
      columnName: 'Rating',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return workspaceFieldOptions.rating_values
            .map((value) => ({
              columnKey: ACCOUNT_COLUMNS.RATING,
              filterKey: value,
              filterName: value,
              filterOptionNode: value,
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [ACCOUNT_COLUMNS.SIZE]: {
      columnKey: ACCOUNT_COLUMNS.SIZE,
      columnName: 'Size',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return workspaceFieldOptions.size_values
            .map((value) => ({
              columnKey: ACCOUNT_COLUMNS.SIZE,
              filterKey: value,
              filterName: value,
              filterOptionNode: value,
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [ACCOUNT_COLUMNS.PLAN]: {
      columnKey: ACCOUNT_COLUMNS.PLAN,
      columnName: 'Plan',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return workspaceFieldOptions.plan_values
            .map((value) => ({
              columnKey: ACCOUNT_COLUMNS.PLAN,
              filterKey: value,
              filterName: value,
              filterOptionNode: value,
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [ACCOUNT_COLUMNS.TYPE]: {
      columnKey: ACCOUNT_COLUMNS.TYPE,
      columnName: 'Type',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return workspaceFieldOptions.account_type_values
            .map((value) => ({
              columnKey: ACCOUNT_COLUMNS.TYPE,
              filterKey: value,
              filterName: value,
              filterOptionNode: value,
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [ACCOUNT_COLUMNS.PRIMARY_USECASE]: {
      columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
      columnName: 'Primary Usecase',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return workspaceFieldOptions.account_primary_usecase_values
            .map((value) => ({
              columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
              filterKey: value,
              filterName: value,
              filterOptionNode: value,
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [ACCOUNT_COLUMNS.STAGE]: {
      columnKey: ACCOUNT_COLUMNS.STAGE,
      columnName: 'Stage',
      isMultiValueField: false,
      filterItemOptions: {
        type: 'select',
        filterItemFetcher: async (params) => {
          const searchValue = params?.searchValue?.toLocaleLowerCase();
          return workspaceFieldOptions.account_stage_values
            .map((value) => ({
              columnKey: ACCOUNT_COLUMNS.STAGE,
              filterKey: value,
              filterName: value,
              filterOptionNode: value,
            }))
            .filter((item) => {
              if (!searchValue) return true;
              return item.filterName.toLocaleLowerCase().includes(searchValue);
            });
        },
      },
    },
    [ACCOUNT_COLUMNS.ALIAS]: {
      columnKey: ACCOUNT_COLUMNS.ALIAS,
      columnName: 'Alias',
      isMultiValueField: false,
      hide: true,
      filterItemOptions: {
        type: 'pagination',
        filterItemFetcher: async (params) => {
          const cursor = params?.cursor;
          const searchValue = params?.searchValue;
          const queryObj: QueryObj = {
            account_name: searchValue || null,
            cursor: cursor || null,
            direction: 'next',
            sortby: 'name',
            order: 'asc',
          };
          const queryString = buildObjQuery(queryObj);
          const res = await service.account.fetchCompanyList(queryString);
          return res.map((company) => ({
            columnKey: ACCOUNT_COLUMNS.ALIAS,
            filterKey: company.alias,
            filterName: company.name,
            filterOptionNode: `${company.name} (${company.alias})`,
            cursor: company.paginate_token,
          }));
        },
      },
    },
  };

  return fieldOptions;
};
